import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentJobStudentApplication } from '@edtech-ui/core/models/student-job-management/get-all-my-applications';
import { StudentGetJobApplicationSubmittedData } from '@edtech-ui/core/models/student-job-management/student-application-submitted';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-jobs-hiring-process',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule],
	templateUrl: './jobs-hiring-process.component.html',
	styleUrl: './jobs-hiring-process.component.scss'
})
export class JobsHiringProcessComponent implements OnInit, OnDestroy {

	jobId!: string;
	subscription = new Subscription();
	isLoading: boolean = false;
	jobSubmitted: StudentGetJobApplicationSubmittedData | null = null;
	jobData: StudentJobStudentApplication[] = [];
	total: number = 0;
	totalPages: number = 0;
	page: number = 1;
	limit: number = 10;
	currentApplication: StudentJobStudentApplication | null = null;
	assessmentUrl: string | null = null;
	interviewUrl: string | null = null;
	buttonText = '';
	title = '';
	hasAssessment = false;
	hasInterview = false;
	description = '';
	assessmentEndDate: string | null = null;
	interviewEndDate: string | null = null;


	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private studentJobService: StudentJobService,
		private toastr: ToastrService
	) { }


	ngOnInit(): void {
		this.subscription.add(
			this.route.paramMap.subscribe(params => {
				this.jobId = params.get('jobId') || '';
				if (this.jobId) {
					this.getStudentJobApplicationSubmitted();
					this.getAllStudentApplications();
				}
			})
		);
	}


	getStudentJobApplicationSubmitted() {
		this.isLoading = true;
		this.subscription.add(
			this.studentJobService.getStudentJobApplicationSubmitted(this.jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.jobSubmitted = response.Data;
						if (!this.jobSubmitted.hiring_process || this.jobSubmitted.hiring_process.length === 0) {
							this.jobSubmitted.hiring_process = [
								'AI-Proctored Assessment',
								'AI-Proctored Video Interview',
								'Recruiter Review & Final Decision'
							];
						}
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		)
	}



	goToMyApplicationPage() {
		this.router.navigate(['student-dashboard/jobs/my-applications']);
	}


	goToJobHome() {
		this.router.navigate(['student-dashboard/jobs']);
	}


	getStageLabel(step: string): string {
		const value = step.toLowerCase();

		if (value.includes('assessment')) {
			return 'Assessment';
		}

		if (value.includes('interview')) {
			return 'Interview';
		}

		if (value.includes('review') || value.includes('result')) {
			return 'Result';
		}
		return 'Stage';
	}


	getStageIcon(step: string): string {
		const value = step.toLowerCase();

		if (value.includes('assessment')) {
			return 'bi bi-clipboard-check';
		}

		if (value.includes('interview')) {
			return 'bi bi-camera-video';
		}

		if (value.includes('review') || value.includes('result')) {
			return 'bi bi-award';
		}

		return 'bi bi-check-circle';
	}


	getStageTitle(step: string): string {
		const value = step.toLowerCase();

		if (value.includes('assessment')) {
			return 'Take up the proctored assessment';
		}

		if (value.includes('interview')) {
			return 'Proctored AI interview';
		}

		if (value.includes('review') || value.includes('result')) {
			return 'Recruiter Review & Final Decision';
		}

		return 'Stage';
	}

	getStageDescription(step: string): string {
		const value = step.toLowerCase();

		if (value.includes('assessment')) {
			return 'Begin with a focused, AI-proctored assessment designed to fairly evaluate your skills for this role. Find a quiet space, keep your camera on, and complete it in a single sitting.';
		}

		if (value.includes('interview')) {
			return 'Unlocks automatically once your assessment is completed. You\'ll be evaluated on communication, technical knowledge, and confidence.';
		}

		if (value.includes('review') || value.includes('result')) {
			return 'Our recruiters will review your overall performance and communicate the final hiring decision.';
		}
		return 'Complete this stage successfully to move to the next round.';
	}

	isStepCompleted(step: string): boolean {
		if (!this.currentApplication) return false;
		const value = step.toLowerCase();
		const completedStatuses = ['completed', 'graded', 'passed', 'failed', 'pass', 'fail'];

		if (value.includes('assessment')) {
			return completedStatuses.includes(this.currentApplication.assessment?.status?.toLowerCase() || '');
		}
		if (value.includes('interview')) {
			return completedStatuses.includes(this.currentApplication.interview?.status?.toLowerCase() || '');
		}
		if (value.includes('review') || value.includes('result')) {
			const appStatus = this.currentApplication.status?.toLowerCase() || '';
			return ['hired', 'rejected', 'selected', 'not_selected'].includes(appStatus);
		}
		return false;
	}

	getCurrentStageNumber(): number {
		if (!this.jobSubmitted || !this.jobSubmitted.hiring_process) return 1;
		let stage = 1;
		for (const step of this.jobSubmitted.hiring_process) {
			if (this.isStepCompleted(step)) {
				stage++;
			} else {
				break;
			}
		}
		const maxStage = (this.jobSubmitted.hiring_process.length || 0) + 1;
		return stage > maxStage ? maxStage : stage;
	}

	getAllStudentApplications() {
		this.isLoading = true;
		this.subscription.add(
			this.studentJobService.getAllStudentApplications(this.page, this.limit).pipe(
				tap(response => {
					if (response.Success) {
						this.jobData = response.Data?.applications || [];
						this.currentApplication = this.jobData.find(app => app.job_id === this.jobId) || null;
						this.assessmentEndDate = this.currentApplication?.assessment?.end_date || null;
						this.interviewEndDate = this.currentApplication?.interview?.end_date || null;

						if (this.currentApplication) {
							this.assessmentEndDate = this.currentApplication.assessment?.end_date || null;
							this.interviewEndDate = this.currentApplication.interview?.end_date || null;
							this.hasAssessment = this.currentApplication.assessment?.status === 'pending';
							this.hasInterview = this.currentApplication.interview?.status === 'pending';
							this.assessmentUrl = null;
							this.interviewUrl = null;
							this.buttonText = '';
							this.title = '';
							this.description = '';
							if (this.hasAssessment) {
								this.assessmentUrl = this.currentApplication.assessment?.quiz_link_url || null;
								this.buttonText = 'Start Assessment';
								this.title = 'Take your proctored assessment';

								this.description = this.hasInterview
									? 'Complete it now to unlock your AI interview. It only takes a few minutes.'
									: 'Complete your AI-proctored assessment to continue your hiring journey.';
							}
							else if (this.hasInterview) {
								this.interviewUrl = this.currentApplication.interview?.interview_url || null;
								this.buttonText = 'Start Interview';
								this.title = 'Take your AI interview';
								this.description = 'Complete your AI-Interview to continue your hiring journey.'
							}
						}
						this.total = response.Data?.total || 0;
						this.page = response.Data?.page || 1;
						this.limit = response.Data?.limit || 10;
						this.totalPages = response.Data?.total_pages || 0;
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	startNextStep() {
		if (!this.currentApplication) return;
		if (this.assessmentUrl) {
			const endDate = this.currentApplication.assessment?.end_date;
			if (endDate) {
				const now = new Date();
				const assessmentEndDate = new Date(endDate);
				if (now >= assessmentEndDate) {
					this.toastr.error('This assessment link is no longer active', 'Assessment link expired');
					return;
				}
			}
			return this.openAssessment(this.assessmentUrl);
		}

		if (this.interviewUrl) {
			const endDate = this.currentApplication.interview?.end_date;
			if (endDate) {
				const now = new Date();
				const interviewEndDate = new Date(endDate);
				if (now >= interviewEndDate) {
					this.toastr.error('This interview link is no longer active', 'Interview link expired');
					return;
				}
			}
			return this.openInterview(this.interviewUrl);
		}
	}


	openAssessment(url: string | null) {
		if (!url) return;
		try {
			const assessmentUrl = new URL(url);
			assessmentUrl.searchParams.set('isStudentDashboard', 'true');
			window.open(assessmentUrl.toString(), '_blank');
		} catch (error) {
			console.error('Invalid assessment URL:', url);
			this.toastr.error('Invalid assessment URL');
		}
	}


	openInterview(url: string | null) {
		if (!url) return;
		try {
			const interviewUrl = new URL(url);
			interviewUrl.searchParams.set('isStudentDashboard', 'true');
			window.open(interviewUrl.toString(), '_blank');
		} catch (error) {
			console.error('Invalid interview URL:', url);
			this.toastr.error('Invalid interview URL');
		}
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}